import type { RecommendedKeywordsResponse } from '../api/opportunities';

export const MIN_RECOMMENDED_KEYWORD_CONFIDENCE_PCT = 5;
export const DEFAULT_AUTO_MATCH_KEYWORD_LIMIT = 10;
export const DEFAULT_SUGGESTION_CHIP_LIMIT = 8;

export function normalizeMonitorKeyword(value: string): string {
  return value.trim().toLowerCase();
}

export function mergeMonitorKeywords(existing: string[], incoming: string[]): string[] {
  const seen = new Set(existing.map(normalizeMonitorKeyword));
  const merged = [...existing];

  for (const keyword of incoming) {
    const trimmed = keyword.trim();
    if (!trimmed) continue;
    const key = normalizeMonitorKeyword(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(trimmed);
  }

  return merged;
}

export function extractTopMonitorKeywords(
  data: Pick<RecommendedKeywordsResponse, 'keywords' | 'service_keyword_groups'> | null | undefined,
  limit = DEFAULT_AUTO_MATCH_KEYWORD_LIMIT
): string[] {
  if (!data) return [];

  const results: string[] = [];
  const seen = new Set<string>();

  const add = (keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    const key = normalizeMonitorKeyword(trimmed);
    if (seen.has(key)) return;
    seen.add(key);
    results.push(trimmed);
  };

  for (const group of data.service_keyword_groups ?? []) {
    for (const sub of group.subcategories ?? []) {
      if (sub.title) add(sub.title);
      if (results.length >= limit) return results.slice(0, limit);
    }
  }

  const sortedKeywords = [...(data.keywords ?? [])]
    .filter((kw) => (kw.confidence_pct ?? 0) > MIN_RECOMMENDED_KEYWORD_CONFIDENCE_PCT)
    .sort((a, b) => (b.confidence_pct ?? 0) - (a.confidence_pct ?? 0));

  for (const kw of sortedKeywords) {
    add(kw.keyword);
    if (results.length >= limit) break;
  }

  return results.slice(0, limit);
}

export function getUnselectedSuggestionChips(
  data: Pick<RecommendedKeywordsResponse, 'keywords'> | null | undefined,
  selectedKeywords: string[],
  limit = DEFAULT_SUGGESTION_CHIP_LIMIT
): Array<{ keyword: string; confidence_pct?: number }> {
  if (!data?.keywords?.length) return [];

  const selected = new Set(selectedKeywords.map(normalizeMonitorKeyword));

  return [...data.keywords]
    .filter((kw) => (kw.confidence_pct ?? 0) > MIN_RECOMMENDED_KEYWORD_CONFIDENCE_PCT)
    .sort((a, b) => (b.confidence_pct ?? 0) - (a.confidence_pct ?? 0))
    .filter((kw) => !selected.has(normalizeMonitorKeyword(kw.keyword)))
    .slice(0, limit)
    .map((kw) => ({ keyword: kw.keyword, confidence_pct: kw.confidence_pct }));
}
